import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLocale } from "../contexts/LocaleContext";
import {
  Container,
  Typography,
  Box,
  Button,
  CircularProgress,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import FlagIcon from "@mui/icons-material/Flag";
import MicIcon from "@mui/icons-material/Mic";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import BlockIcon from "@mui/icons-material/Block";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import { getCall, getTeamCall } from "../utils/firebase";
import { getSampleReports } from "../data/sampleReport";
import CircularGauge from "../components/CircularGauge";


function formatDate(timestamp) {
  if (!timestamp) return "—";
  const d = new Date(timestamp);
  return d.toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const MOMENT_COLORS = {
  objection: { bg: "#fee2e2", text: "#dc2626" },
  question: { bg: "#dbeafe", text: "#2563eb" },
  discovery: { bg: "#dbeafe", text: "#2563eb" },
  closing: { bg: "#dcfce7", text: "#16a34a" },
  rapport: { bg: "#f3e8ff", text: "#7c3aed" },
  "buying-signal": { bg: "#dcfce7", text: "#15803d" },
  risk: { bg: "#fef3c7", text: "#b45309" },
};

function closureBand(pct, t) {
  if (pct >= 80) return { label: t("callDetailPage.leadInterestStrong"), bg: "#ecfdf5", text: "#059669", border: "#a7f3d0" };
  if (pct >= 60) return { label: t("callDetailPage.leadInterestWarm"), bg: "#ecfdf5", text: "#059669", border: "#a7f3d0" };
  if (pct >= 40) return { label: t("callDetailPage.leadInterestMixed"), bg: "#fef3c7", text: "#b45309", border: "#fde68a" };
  if (pct >= 20) return { label: t("callDetailPage.leadInterestCool"), bg: "#fef3c7", text: "#b45309", border: "#fde68a" };
  return { label: t("callDetailPage.leadInterestDead"), bg: "#fee2e2", text: "#dc2626", border: "#fecaca" };
}

export default function CallDetailPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const memberId = searchParams.get("memberId");
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { localizePath } = useLocale();
  const isSample = id === "sample";
  const sampleVariant = searchParams.get("variant") === "rough" ? "rough" : "strong";
  const [call, setCall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isSample) {
      setCall(getSampleReports(i18n.language)[sampleVariant]);
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const result = memberId
          ? await getTeamCall({ memberId, callId: id })
          : await getCall({ callId: id });
        setCall(result.data);
      } catch (err) {
        console.error("Failed to load call:", err);
        setError(t("callDetailPage.callNotFound"));
      } finally {
        setLoading(false);
      }
    })();
  }, [id, memberId, t, isSample, sampleVariant, i18n.language]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={10}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !call) {
    return (
      <Container maxWidth="lg" sx={{ py: 5 }}>
        <Typography>{error || t("callDetailPage.callNotFound")}</Typography>
        <Button onClick={() => navigate(memberId ? localizePath("/reports") : localizePath("/"))} sx={{ mt: 2 }}>
          {memberId ? t("callDetailPage.backToReports") : t("callDetailPage.backToDashboard")}
        </Button>
      </Container>
    );
  }

  // No-show: the prospect never joined, so the call was not scored. Show a clear
  // explanation instead of an empty report shell.
  if (call.status === "no_show") {
    const noShow = call.noShow || {};
    const guestList = Array.isArray(noShow.guests) ? noShow.guests.filter(Boolean) : [];
    return (
      <Box sx={{ bgcolor: "#ffffff", minHeight: "100vh" }}>
        <Container maxWidth="lg" sx={{ py: 5, px: { xs: 2, sm: 3 } }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(memberId ? localizePath("/reports") : localizePath("/"))}
            sx={{ mb: 3, color: "#6b7280", textTransform: "none" }}
          >
            {t("callDetailPage.back")}
          </Button>
          <Box sx={{ textAlign: "center", py: 8, maxWidth: 560, mx: "auto" }}>
            <BlockIcon sx={{ fontSize: 56, color: "#b45309", mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 600, color: "#111827", fontSize: 26, mb: 1 }}>
              {call.title || t("callDetailPage.untitledMeeting")}
            </Typography>
            <Typography sx={{ color: "#6b7280", fontSize: 14, mb: 3 }}>
              {formatDate(call.timestamp)}
            </Typography>
            <Chip
              label={t("callCard.status.no_show")}
              color="warning"
              sx={{ mb: 2, fontWeight: 600 }}
            />
            <Typography sx={{ color: "#374151", fontSize: 15, mb: 1 }}>
              {guestList.length > 0
                ? t("callDetailPage.noShow.bodyWithGuest", { guest: guestList.join(", ") })
                : t("callDetailPage.noShow.body")}
            </Typography>
            <Typography sx={{ color: "#9ca3af", fontSize: 13 }}>
              {t("callDetailPage.noShow.notScored")}
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  const { title, timestamp, analysis, transcript = [] } = call;
  const { score, summary, keyMoments = [], improvementTips = [], talkRatio, questionCount, interruptionRate, silenceRatio, nextSteps, closurePct, closureReason } = analysis || {};
  const closure = closurePct != null && !Number.isNaN(Number(closurePct))
    ? { pct: Number(closurePct), band: closureBand(Number(closurePct), t) }
    : null;

  return (
    <Box sx={{ bgcolor: "#ffffff", minHeight: "100vh" }}>
      <Container maxWidth="lg" sx={{ py: 5, px: { xs: 2, sm: 3 } }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(memberId ? localizePath("/reports") : localizePath("/"))}
          sx={{ mb: 3, color: "#6b7280", textTransform: "none" }}
        >
          {t("callDetailPage.back")}
        </Button>

        {isSample && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              borderRadius: 2,
              bgcolor: "#eef2ff",
              border: "1px solid #c7d2fe",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1.5,
            }}
          >
            <Typography sx={{ fontSize: 14, color: "#3730a3", fontWeight: 500 }}>
              {t("callDetailPage.sampleBanner")}
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate(localizePath("/"))}
              sx={{ bgcolor: "#4338ca", textTransform: "none", "&:hover": { bgcolor: "#3730a3" } }}
            >
              {t("callDetailPage.sampleConnectCta")}
            </Button>
          </Box>
        )}

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
          mb={4}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontFamily: '"Cal Sans", "Inter", sans-serif',
                fontWeight: 600,
                letterSpacing: "-0.02em",
                color: "#111827",
                fontSize: 28,
              }}
            >
              {title || t("callDetailPage.untitledMeeting")}
            </Typography>
            <Typography sx={{ color: "#6b7280", fontSize: 14, mt: 0.5 }}>
              {formatDate(timestamp)}
            </Typography>
          </Box>
          {score != null && (
            <CircularGauge score={score} size={56} strokeWidth={4} showLabel label="Score" />
          )}
        </Box>

        {closure && (
          <Box
            sx={{
              p: 2.5,
              mb: 4,
              bgcolor: closure.band.bg,
              borderRadius: 2,
              border: `1px solid ${closure.band.border}`,
            }}
          >
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: closure.band.text,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                mb: 0.5,
              }}
            >
              {t("callDetailPage.leadInterest")}
            </Typography>
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={1.5}>
              <Typography sx={{ fontSize: 32, fontWeight: 700, color: closure.band.text, lineHeight: 1 }}>
                {closure.pct}%
              </Typography>
              <Chip
                label={closure.band.label}
                size="small"
                sx={{
                  bgcolor: "#ffffff",
                  color: closure.band.text,
                  fontWeight: 600,
                  fontSize: 12,
                }}
              />
            </Box>
            {closureReason && (
              <Typography sx={{ mt: 1.25, fontSize: 13, color: "#374151", lineHeight: 1.5 }}>
                {closureReason}
              </Typography>
            )}
          </Box>
        )}

        {summary && (
          <Box
            sx={{
              p: 3,
              mb: 4,
              bgcolor: "#f9fafb",
              borderRadius: 2,
              border: "1px solid #e5e7eb",
            }}
          >
            <Typography sx={{ fontSize: 14, color: "#374151", lineHeight: 1.6 }}>
              {summary}
            </Typography>
          </Box>
        )}

        {talkRatio && (
          <Box
            sx={{
              p: 2.5,
              mb: 4,
              bgcolor: talkRatio.inSweetSpot ? "#ecfdf5" : "#f9fafb",
              borderRadius: 2,
              border: `1px solid ${talkRatio.inSweetSpot ? "#a7f3d0" : "#e5e7eb"}`,
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                color: "#111827",
                fontSize: 16,
                mb: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <MicIcon sx={{ fontSize: 20 }} />
              {t("callDetailPage.percentageTalk")}
            </Typography>
            <Box display="flex" flexWrap="wrap" alignItems="center" gap={2}>
              <Typography sx={{ fontSize: 28, fontWeight: 700, color: "#111827" }}>
                {talkRatio.repTalkPct}%
              </Typography>
              {talkRatio.inSweetSpot ? (
                <Box display="flex" alignItems="center" gap={0.75} sx={{ color: "#059669" }}>
                  <CheckCircleIcon sx={{ fontSize: 22 }} />
                  <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                    {t("callDetailPage.sweetSpotSales")} ({talkRatio.sweetSpotRange})
                  </Typography>
                </Box>
              ) : (
                <Typography sx={{ fontSize: 14, color: "#6b7280" }}>
                  {t("callDetailPage.sweetSpotSalesDesc", { range: talkRatio.sweetSpotRange })}
                </Typography>
              )}
            </Box>
            {(() => {
              // Backend returns an array of { speaker, pct } (RTDB keys can't hold
              // ".#$/[]" from names). Older/sample data may use a { name: pct } object —
              // normalize both to [speaker, pct] pairs.
              const breakdown = Array.isArray(talkRatio.speakerBreakdown)
                ? talkRatio.speakerBreakdown.map((e) => [e.speaker, e.pct])
                : Object.entries(talkRatio.speakerBreakdown || {});
              if (breakdown.length === 0) return null;
              return (
                <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #e5e7eb" }}>
                  <Typography sx={{ fontSize: 12, color: "#6b7280", mb: 1, fontWeight: 500 }}>
                    {t("callDetailPage.speakerBreakdown")}
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {breakdown.map(([speaker, pct]) => (
                      <Chip
                        key={speaker}
                        label={`${speaker}: ${pct}%`}
                        size="small"
                        sx={{ fontSize: 12 }}
                      />
                    ))}
                  </Box>
                </Box>
              );
            })()}
          </Box>
        )}

        {nextSteps && (
          <Box
            sx={{
              p: 2.5,
              mb: 4,
              bgcolor: nextSteps.hasNextSteps ? "#ecfdf5" : "#f9fafb",
              borderRadius: 2,
              border: `1px solid ${nextSteps.hasNextSteps ? "#a7f3d0" : "#e5e7eb"}`,
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                color: "#111827",
                fontSize: 16,
                mb: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <PlaylistAddCheckIcon sx={{ fontSize: 20 }} />
              {t("callDetailPage.clearNextSteps")}
            </Typography>
            <Box display="flex" flexWrap="wrap" alignItems="center" gap={2}>
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#111827" }}>
                {nextSteps.hasNextSteps ? t("callDetailPage.yes") : t("callDetailPage.no")}
              </Typography>
              {nextSteps.hasNextSteps ? (
                <Box display="flex" alignItems="center" gap={0.75} sx={{ color: "#059669" }}>
                  <CheckCircleIcon sx={{ fontSize: 22 }} />
                  <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                    {t("callDetailPage.concreteNextStep")}
                  </Typography>
                </Box>
              ) : (
                <Typography sx={{ fontSize: 14, color: "#6b7280" }}>
                  {t("callDetailPage.noClearNextStep")}
                </Typography>
              )}
            </Box>
            {nextSteps.hasNextSteps && nextSteps.description && (
              <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #e5e7eb" }}>
                <Typography sx={{ fontSize: 12, color: "#6b7280", mb: 0.5, fontWeight: 500 }}>
                  {t("callDetailPage.agreedNextStep")}
                </Typography>
                <Typography sx={{ fontSize: 14, color: "#374151" }}>
                  {nextSteps.description}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {(questionCount || interruptionRate || silenceRatio) && (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {questionCount && (
            <Grid item xs={12} sm={4}>
              <Box
                sx={{
                  p: 2.5,
                  height: "100%",
                  bgcolor: questionCount.inTarget ? "#ecfdf5" : "#f9fafb",
                  borderRadius: 2,
                  border: `1px solid ${questionCount.inTarget ? "#a7f3d0" : "#e5e7eb"}`,
                }}
              >
                <Typography sx={{ fontWeight: 600, color: "#111827", fontSize: 16, mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                  <HelpOutlineIcon sx={{ fontSize: 20 }} />
                  {t("callDetailPage.questionCount")}
                </Typography>
                <Typography sx={{ fontSize: 28, fontWeight: 700, color: "#111827" }}>
                  {questionCount.count}
                </Typography>
                <Typography sx={{ fontSize: 14, color: "#6b7280", mt: 0.5 }}>
                  {t("callDetailPage.target")}: {questionCount.target}
                </Typography>
                {questionCount.inTarget ? (
                  <Box display="flex" alignItems="center" gap={0.5} sx={{ color: "#059669", mt: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: 18 }} />
                    <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{t("callDetailPage.inTarget")}</Typography>
                  </Box>
                ) : null}
              </Box>
            </Grid>
          )}
          {interruptionRate && (
            <Grid item xs={12} sm={4}>
              <Box
                sx={{
                  p: 2.5,
                  height: "100%",
                  bgcolor: interruptionRate.ratePct < 10 ? "#ecfdf5" : "#f9fafb",
                  borderRadius: 2,
                  border: `1px solid ${interruptionRate.ratePct < 10 ? "#a7f3d0" : "#e5e7eb"}`,
                }}
              >
                <Typography sx={{ fontWeight: 600, color: "#111827", fontSize: 16, mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                  <BlockIcon sx={{ fontSize: 20 }} />
                  {t("callDetailPage.interruptionRate")}
                </Typography>
                <Typography sx={{ fontSize: 28, fontWeight: 700, color: "#111827" }}>
                  {interruptionRate.ratePct}%
                </Typography>
                <Typography sx={{ fontSize: 14, color: "#6b7280", mt: 0.5 }}>
                  Lower is better
                </Typography>
                {interruptionRate.ratePct < 10 ? (
                  <Box display="flex" alignItems="center" gap={0.5} sx={{ color: "#059669", mt: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: 18 }} />
                    <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{t("callDetailPage.good")}</Typography>
                  </Box>
                ) : null}
              </Box>
            </Grid>
          )}
          {silenceRatio && (
            <Grid item xs={12} sm={4}>
              <Box
                sx={{
                  p: 2.5,
                  height: "100%",
                  bgcolor: silenceRatio.inSweetSpot ? "#ecfdf5" : "#f9fafb",
                  borderRadius: 2,
                  border: `1px solid ${silenceRatio.inSweetSpot ? "#a7f3d0" : "#e5e7eb"}`,
                }}
              >
                <Typography sx={{ fontWeight: 600, color: "#111827", fontSize: 16, mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                  <PauseCircleOutlineIcon sx={{ fontSize: 20 }} />
                  {t("callDetailPage.silencePauseRatio")}
                </Typography>
                <Typography sx={{ fontSize: 28, fontWeight: 700, color: "#111827" }}>
                  {silenceRatio.silencePct}%
                </Typography>
                <Typography sx={{ fontSize: 14, color: "#6b7280", mt: 0.5 }}>
                  {t("callDetailPage.sweetSpot")}: {silenceRatio.sweetSpotRange}
                </Typography>
                {silenceRatio.inSweetSpot ? (
                  <Box display="flex" alignItems="center" gap={0.5} sx={{ color: "#059669", mt: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: 18 }} />
                    <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{t("callDetailPage.inSweetSpot")}</Typography>
                  </Box>
                ) : null}
              </Box>
            </Grid>
          )}
        </Grid>
        )}

        {interruptionRate?.moments?.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography
              sx={{
                fontFamily: '"Cal Sans", "Inter", sans-serif',
                fontWeight: 600,
                color: "#111827",
                fontSize: 18,
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <BlockIcon sx={{ fontSize: 20 }} />
              {t("callDetailPage.whereInterrupted")}
            </Typography>
            <Box display="flex" flexDirection="column" gap={1.5}>
              {interruptionRate.moments.map((m, i) => (
                <Box
                  key={i}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid #e5e7eb",
                    bgcolor: "#ffffff",
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#6b7280" }}>
                      {m.timestamp}
                    </Typography>
                    <Chip
                      label={m.overlapSeconds != null ? t("callDetailPage.overlapSeconds", { seconds: m.overlapSeconds }) : t("callDetailPage.overlap")}
                      size="small"
                      sx={{
                        bgcolor: "#fee2e2",
                        color: "#dc2626",
                        fontSize: 11,
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                  <Typography sx={{ fontSize: 13, color: "#6b7280", mb: 0.5 }}>
                    <strong>{m.interruptedSpeaker}</strong> was saying: &quot;{m.interruptedText}{m.interruptedText.length >= 120 ? "…" : ""}&quot;
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: "#374151" }}>
                    You said: &quot;{m.repText}{m.repText.length >= 120 ? "…" : ""}&quot;
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        <Grid container spacing={4}>
          <Grid item xs={12} lg={7}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: '"Cal Sans", "Inter", sans-serif',
                fontWeight: 600,
                color: "#111827",
                mb: 2,
                fontSize: 18,
              }}
            >
              {t("callDetailPage.transcript")}
            </Typography>
            <Box
              sx={{
                p: 2,
                bgcolor: "#f9fafb",
                borderRadius: 2,
                border: "1px solid #e5e7eb",
                maxHeight: 500,
                overflowY: "auto",
              }}
            >
              {transcript.length === 0 ? (
                <Typography sx={{ color: "#9ca3af", fontSize: 14 }}>
                  {t("callDetailPage.noTranscript")}
                </Typography>
              ) : (
                transcript.map((seg, i) => (
                  <Box key={i} sx={{ mb: 1.5 }}>
                    <Box display="flex" gap={1} alignItems="baseline">
                      <Typography
                        component="span"
                        sx={{ fontWeight: 600, fontSize: 13, color: "#111827" }}
                      >
                        {seg.speaker}
                      </Typography>
                      {seg.startTime != null && (
                        <Typography
                          component="span"
                          sx={{ fontSize: 11, color: "#9ca3af" }}
                        >
                          {formatSec(seg.startTime)}
                        </Typography>
                      )}
                    </Box>
                    <Typography sx={{ fontSize: 14, color: "#374151", lineHeight: 1.5 }}>
                      {seg.text}
                    </Typography>
                  </Box>
                ))
              )}
            </Box>
          </Grid>

          <Grid item xs={12} lg={5}>
            {keyMoments.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: '"Cal Sans", "Inter", sans-serif',
                    fontWeight: 600,
                    color: "#111827",
                    mb: 2,
                    fontSize: 18,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <FlagIcon sx={{ fontSize: 20 }} />
                  {t("callDetailPage.keyMoments")}
                </Typography>
                <Box display="flex" flexDirection="column" gap={1}>
                  {keyMoments.map((m, i) => {
                    const colors = MOMENT_COLORS[m.type] || MOMENT_COLORS.rapport;
                    return (
                      <Box
                        key={i}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: "1px solid #e5e7eb",
                          bgcolor: "#ffffff",
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <Chip
                            label={t(`callDetailPage.momentType.${m.type}`) || m.type}
                            size="small"
                            sx={{
                              bgcolor: colors.bg,
                              color: colors.text,
                              fontSize: 11,
                              fontWeight: 600,
                              textTransform: "capitalize",
                            }}
                          />
                          {m.timestamp && (
                            <Typography sx={{ fontSize: 11, color: "#9ca3af" }}>
                              {m.timestamp}
                            </Typography>
                          )}
                        </Box>
                        <Typography sx={{ fontSize: 13, color: "#374151" }}>
                          {m.text}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {improvementTips.length > 0 && (
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: '"Cal Sans", "Inter", sans-serif',
                    fontWeight: 600,
                    color: "#111827",
                    mb: 2,
                    fontSize: 18,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <LightbulbIcon sx={{ fontSize: 20 }} />
                  {t("callDetailPage.improvementTips")}
                </Typography>
                <List dense disablePadding>
                  {improvementTips.map((tip, i) => (
                    <ListItem key={i} disableGutters sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 24 }}>
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            bgcolor: "#111827",
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={tip}
                        primaryTypographyProps={{
                          sx: { fontSize: 14, color: "#374151" },
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

function formatSec(seconds) {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}
